import type { ReactNode } from "react";

interface BillingPageShellProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function BillingPageShell({
  title,
  subtitle,
  actions,
  children,
}: BillingPageShellProps) {
  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-flex d-block align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h6 className="mb-0">{title}</h6>
            {subtitle && <p className="text-muted fs-13 mb-0">{subtitle}</p>}
          </div>
          {actions && (
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              {actions}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  hint?: string;
  iconClass?: string;
  variant?: "primary" | "success" | "warning" | "danger";
}

const variantClass: Record<NonNullable<StatCardProps["variant"]>, string> = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

export function StatCard({
  label,
  value,
  hint,
  iconClass = "ti ti-receipt",
  variant = "primary",
}: StatCardProps) {
  return (
    <div className="col-xl-3 col-lg-4 col-md-6">
      <div className="card position-relative">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
            <div>
              <p className="mb-1">{label}</p>
              <h6 className="fs-16 fw-semibold mb-0">{value}</h6>
            </div>
            <span className={`avatar ${variantClass[variant]} rounded-circle`}>
              <i className={iconClass} />
            </span>
          </div>
          {hint && <p className="fs-13 mb-0 text-muted">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export function invoiceStatusBadge(status: string) {
  const map: Record<string, string> = {
    PAID: "badge-soft-success",
    REFUNDED: "badge-soft-success",
    ISSUED: "badge-soft-info",
    PARTIAL: "badge-soft-warning",
    UNPAID: "badge-soft-warning",
    OVERDUE: "badge-soft-danger",
    CANCELLED: "badge-soft-danger",
    DRAFT: "badge-soft-secondary",
  };
  return map[status] ?? "badge-soft-info";
}

export function formatMoney(amount: string, currency: string) {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) return amount;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}
